// Owner: page-editor (wave 2, plan §4.2, §9.9). The page editor's keys in the one command registry:
// ⌘D duplicates the block, ⌘⇧↑/↓ move blocks, ⌘⌥M comments, ⌘K edits the link of a selection (and
// leaves ⌘K to the command bar otherwise), ⌘S saves now, and the text shortcuts (headings, lists,
// marks) are listed in the ? sheet. ProseMirror handles the text keys first while typing; the
// registry runs them from the sheet and ⌘K. Discovered by glob: keep this file tiny.
import { defineCommand, defineScope } from '@dataroom/interaction';
import { commentCommand } from '../../collab/commands';
import { saveNowCommand } from '../../editor-kit/commands';
import { defineAppCommands } from '../../extensions/commands';
import { pageEditorCommandTitle as title } from './command-titles';

export type PageEditorCommandContext = {
  duplicate: () => boolean | void;
  move: (dir: -1 | 1) => boolean | void;
  comment: () => boolean | void;
  canLink: () => boolean;
  link: () => boolean | void;
  saveNow: () => Promise<boolean>;
  format: (id: PageFormat) => boolean | void;
  editable: () => boolean;
};

export type PageFormat =
  | 'paragraph'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'bulletList'
  | 'orderedList'
  | 'taskList'
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strike'
  | 'code';

export const pageEditorScope = defineScope<PageEditorCommandContext>({
  id: 'pageEditor',
  kind: 'editor',
  title: () => title('scope'),
});

const block = (
  id: string,
  title: () => string,
  shortcut: string,
  run: (c: PageEditorCommandContext) => boolean | void,
  group?: () => string,
) =>
  defineCommand<PageEditorCommandContext>({
    id: `page-editor.${id}`,
    title,
    shortcut,
    scope: pageEditorScope,
    textSafe: true,
    when: (c) => c.editable(),
    run,
    ...(group ? { group } : {}),
  });

const blocks = () => title('groupBlocks');
const text = () => title('groupText');

export default defineAppCommands({
  id: 'pageEditor',
  loadTitles: () => import('./command-titles-ready').then((m) => m.fillCommandTitles()),
  scopes: [pageEditorScope],
  commands: [
    block(
      'duplicate',
      () => title('duplicate'),
      'mod+d',
      (c) => c.duplicate(),
      blocks,
    ),
    block(
      'moveUp',
      () => title('moveUp'),
      'mod+shift+arrowup',
      (c) => c.move(-1),
      blocks,
    ),
    block(
      'moveDown',
      () => title('moveDown'),
      'mod+shift+arrowdown',
      (c) => c.move(1),
      blocks,
    ),
    commentCommand(pageEditorScope, 'page-editor.comment'),
    defineCommand<PageEditorCommandContext>({
      id: 'page-editor.link',
      title: () => title('link'),
      shortcut: 'mod+k',
      scope: pageEditorScope,
      textSafe: true,
      overrides: ['app.palette'],
      group: text,
      // Without a selection (or a link at the caret) ⌘K stays the command bar.
      when: (c) => c.editable() && c.canLink(),
      run: (c) => c.link(),
    }),
    saveNowCommand(pageEditorScope, 'page-editor.saveNow'),
    block(
      'text',
      () => title('paragraph'),
      'mod+alt+0',
      (c) => c.format('paragraph'),
      text,
    ),
    block(
      'h1',
      () => title('h1'),
      'mod+alt+1',
      (c) => c.format('h1'),
      text,
    ),
    block(
      'h2',
      () => title('h2'),
      'mod+alt+2',
      (c) => c.format('h2'),
      text,
    ),
    block(
      'h3',
      () => title('h3'),
      'mod+alt+3',
      (c) => c.format('h3'),
      text,
    ),
    block(
      'numbers',
      () => title('orderedList'),
      'mod+shift+7',
      (c) => c.format('orderedList'),
      text,
    ),
    block(
      'bullets',
      () => title('bulletList'),
      'mod+shift+8',
      (c) => c.format('bulletList'),
      text,
    ),
    block(
      'todo',
      () => title('taskList'),
      'mod+shift+9',
      (c) => c.format('taskList'),
      text,
    ),
    block(
      'bold',
      () => title('bold'),
      'mod+b',
      (c) => c.format('bold'),
      text,
    ),
    block(
      'italic',
      () => title('italic'),
      'mod+i',
      (c) => c.format('italic'),
      text,
    ),
    block(
      'underline',
      () => title('underline'),
      'mod+u',
      (c) => c.format('underline'),
      text,
    ),
    block(
      'strike',
      () => title('strike'),
      'mod+shift+s',
      (c) => c.format('strike'),
      text,
    ),
    block(
      'code',
      () => title('code'),
      'mod+e',
      (c) => c.format('code'),
      text,
    ),
  ],
});
